import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  FileText,
  FolderOpen,
  Library,
  Loader2,
  ScrollText,
  Sparkles,
  StickyNote,
  User as UserIcon,
} from 'lucide-react'
import {
  estimateTokensFromChars,
  type EffectiveInstructions,
  type InstructionScope,
  type InstructionSegment,
} from '@/api/docfiller-types'
import { useEffectiveInstructions } from '@/api/queries'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const SCOPE_ICON: Record<InstructionScope, React.ComponentType<{ className?: string }>> = {
  project: FolderOpen,
  document: FileText,
  saved_prompt: Library,
  snippet: StickyNote,
  user: UserIcon,
}

const SCOPE_TONE: Record<
  InstructionScope,
  'neutral' | 'primary' | 'success' | 'warning' | 'info' | 'danger'
> = {
  project: 'info',
  document: 'success',
  saved_prompt: 'primary',
  snippet: 'neutral',
  user: 'warning',
}

const SCOPE_DEFAULT_HEADING: Record<InstructionScope, string> = {
  project: 'Project',
  document: 'Document',
  saved_prompt: 'Saved prompt',
  snippet: 'Snippet',
  user: 'Your prompt',
}

interface Props {
  documentId: string
  savedPromptUuid?: string | null
  userInstructions?: string | null
  /** When true, panel starts collapsed (used in the chat composer to keep
   * the focus on the input). */
  collapsedByDefault?: boolean
}

/**
 * Shows the composed precedence stack for a fill, plus a token-budget bar.
 *
 * The agent reads the layers in the order they're listed here, so the
 * panel doubles as documentation: "this is what we'll send, top-down".
 * Each segment shows its scope, label, char count, and an estimate in
 * tokens (4 chars / token, mirroring the backend's heuristic). The bar
 * colour flips to a warning past ``budget_chars`` and to destructive past
 * ``hard_budget_chars`` — the agent gateway has a much higher ceiling but
 * we surface ours early so users get a chance to trim before the run.
 */
export function EffectiveInstructionsPanel({
  documentId,
  savedPromptUuid,
  userInstructions,
  collapsedByDefault = false,
}: Props) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [collapsed, setCollapsed] = useState(collapsedByDefault)
  const {
    data,
    isLoading,
    error,
  } = useEffectiveInstructions(
    documentId,
    { savedPromptUuid, userInstructions },
    { enabled: !collapsed },
  )

  if (isLoading) {
    return (
      <section
        data-testid="effective-instructions-loading"
        aria-busy="true"
        className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground"
      >
        <Loader2 className="mr-2 inline size-3 animate-spin" aria-hidden />
        Computing instruction precedence…
      </section>
    )
  }

  if (error) {
    return (
      <section
        data-testid="effective-instructions-error"
        role="alert"
        className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive"
      >
        Couldn&apos;t load the instruction precedence:{' '}
        {error instanceof Error ? error.message : String(error)}
      </section>
    )
  }

  if (!data) return null

  return (
    <section
      data-testid="effective-instructions-panel"
      aria-labelledby="effective-instructions-heading"
      className="rounded-lg border border-border bg-muted/40 p-3 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        <h3
          id="effective-instructions-heading"
          className="aurora-eyebrow inline-flex items-center gap-1.5"
        >
          <ScrollText className="size-3" aria-hidden />
          Effective instructions
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls="effective-instructions-body"
          data-testid="effective-instructions-toggle"
        >
          {collapsed ? (
            <>
              <ChevronDown className="size-3.5" aria-hidden />
              Show
            </>
          ) : (
            <>
              <ChevronUp className="size-3.5" aria-hidden />
              Hide
            </>
          )}
        </Button>
      </div>
      {!collapsed && (
        <div id="effective-instructions-body" className="space-y-2">
          <BudgetBar info={data} />
          {data.segments.length === 0 ? (
            <p
              data-testid="effective-instructions-empty"
              className="text-xs text-muted-foreground"
            >
              No saved context yet. The agent will rely only on the source
              document and your chat instructions. Add workspace, project, or
              folder instructions to give it more to work with.
            </p>
          ) : (
            <ol
              data-testid="effective-instructions-segments"
              className="space-y-1.5"
            >
              {data.segments.map((seg, idx) => (
                <SegmentRow
                  key={`${seg.scope}-${idx}`}
                  segment={seg}
                  order={idx + 1}
                  isLast={idx === data.segments.length - 1}
                  expanded={expanded.has(idx)}
                  onToggle={() =>
                    setExpanded((prev) => {
                      const next = new Set(prev)
                      if (next.has(idx)) next.delete(idx)
                      else next.add(idx)
                      return next
                    })
                  }
                />
              ))}
            </ol>
          )}
          <p className="text-[10px] text-muted-foreground/80">
            Precedence runs top → bottom: the most specific layer (your chat
            input) overrides earlier guidance when they conflict.
          </p>
        </div>
      )}
    </section>
  )
}

function BudgetBar({ info }: { info: EffectiveInstructions }) {
  const pct = info.hard_budget_chars
    ? Math.min(100, (info.total_chars / info.hard_budget_chars) * 100)
    : 0
  const over = info.total_chars > info.budget_chars
  const wayOver = info.total_chars > info.hard_budget_chars
  const tone = wayOver ? 'danger' : over ? 'warning' : 'info'
  return (
    <div
      data-testid="effective-instructions-budget"
      data-over={over ? 'true' : 'false'}
      data-tone={tone}
      className="space-y-1"
    >
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          {info.total_chars.toLocaleString()} chars · ~
          {info.estimated_tokens.toLocaleString()} tokens
        </span>
        <span>
          budget {info.budget_chars.toLocaleString()} / hard{' '}
          {info.hard_budget_chars.toLocaleString()}
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-card"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-label="Instruction budget"
      >
        <div
          className={cn(
            'h-full transition-[width]',
            tone === 'danger'
              ? 'bg-destructive'
              : tone === 'warning'
                ? 'bg-warning'
                : 'bg-primary',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      {wayOver && (
        <p
          role="alert"
          data-testid="effective-instructions-over-budget"
          className="inline-flex items-center gap-1 text-[11px] text-destructive"
        >
          <AlertTriangle className="size-3" aria-hidden />
          Over the safe budget — trim a layer before running to keep the
          prompt focused.
        </p>
      )}
      {!wayOver && over && (
        <p
          role="status"
          data-testid="effective-instructions-near-budget"
          className="inline-flex items-center gap-1 text-[11px] text-warning-foreground"
        >
          <AlertTriangle className="size-3" aria-hidden />
          Approaching the budget — consider tightening longer layers.
        </p>
      )}
    </div>
  )
}

function SegmentRow({
  segment,
  order,
  isLast,
  expanded,
  onToggle,
}: {
  segment: InstructionSegment
  order: number
  isLast: boolean
  expanded: boolean
  onToggle: () => void
}) {
  const Icon = SCOPE_ICON[segment.scope]
  const heading = useMemo(
    () => segment.label || SCOPE_DEFAULT_HEADING[segment.scope],
    [segment.label, segment.scope],
  )
  const tokens = estimateTokensFromChars(segment.char_count)
  return (
    <li
      data-testid={`effective-instructions-segment-${segment.scope}`}
      data-order={order}
      className="rounded-md border border-border/70 bg-card px-2.5 py-2"
    >
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-medium text-foreground/90">
              {order}. {heading}
            </span>
            <Badge tone={SCOPE_TONE[segment.scope]} size="xs">
              {segment.scope.replace('_', ' ')}
            </Badge>
            {isLast && segment.scope === 'user' && (
              <Badge tone="success" size="xs">
                <Sparkles className="size-2.5" aria-hidden />
                Most specific
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {segment.char_count.toLocaleString()} chars · ~
            {tokens.toLocaleString()} tokens
          </p>
          {expanded && (
            <pre
              data-testid={`effective-instructions-content-${segment.scope}`}
              className="mt-1.5 max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded border border-border/60 bg-muted/40 p-2 font-sans text-[11px] leading-snug text-foreground/85"
            >
              {segment.content}
            </pre>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? `Hide content for ${heading}`
              : `Show content for ${heading}`
          }
          data-testid={`effective-instructions-expand-${segment.scope}`}
        >
          {expanded ? (
            <ChevronUp className="size-3.5" aria-hidden />
          ) : (
            <ChevronDown className="size-3.5" aria-hidden />
          )}
        </Button>
      </div>
    </li>
  )
}
